import type {GraphStatus} from 'src/types/chatbot';
import {useStore} from 'react-redux';
import {ContentState, Modifier, EditorState} from 'draft-js';
import {setWhatsappDetails} from 'src/action-creators/chatbot';


export const statusLabels: Partial<Record<GraphStatus, string>> = {
  init: 'Draft',
  building: 'Building',
  success: 'Ready for use',
  failed: 'In manual review',
  archived: 'Archived',
};

function getChatbotStagingDomain(hostname: string) {
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];
  return {
    'stage-chatbot': 'stage-1',
    'stage-chatbot-02': 'stage-02',
    'stage-chatbot-03': 'stage-03',
    'stage-chatbot-04': 'stage-04',
    'stage-chatbot-05': 'stage-05',
    'stage-chatbot-06': 'stage-06',
  }[senseStagingDomain];
}

export const useChatbotApiPath = (env: {
  agencySlug: string;
  serverRuntimeEnvironment: string;
  hostname: string;
  chatbotApiUrl: string;
}): string => {
  let chatbot_url;

  const {agencySlug, serverRuntimeEnvironment, chatbotApiUrl, hostname} = env;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
    case 'production':
    case 'development':
      // Extract country code and domain info from chatbotApiUrl
      // Example: api.sensehqchat.sensehq.eu -> agencySlug.sensehqchat.sensehq.eu/api
      // Example: api.sensehqchat.com -> agencySlug.sensehqchat.com/api
      const urlParts = chatbotApiUrl.split('.');
      if (urlParts.length >= 2) {
        // Remove 'api' prefix and join remaining parts
        urlParts.shift();
        chatbot_url = `https://${agencySlug}.${urlParts.join('.')}/api`;
      } else {
        // Fallback to default domain if URL parsing fails
        chatbot_url = `https://${agencySlug}.sensehqchat.com/api`;
      }
      break;
    case 'staging':
    default:
      chatbot_url = `https://${agencySlug}${getChatbotStagingDomain(
        hostname,
      )}.sensehqchat.com/api`;
      break;
  }

  return chatbot_url;
};

export function getChatbotSnippetURL(
  hostname: string,
  agencySlug: string,
  serverRuntimeEnvironment:
    | 'production'
    | 'staging'
    | 'preprod'
    | 'development',
  chatbotApiUrl: string,
): string {
  let chatbot_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
    case 'production':
    case 'development':
      // Extract country code and domain info from chatbotApiUrl
      // Example: api.sensehqchat.sensehq.eu -> agencySlug.sensehqchat.sensehq.eu
      // Example: api.sensehqchat.com -> agencySlug.sensehqchat.com
      const urlParts = chatbotApiUrl.split('.');
      if (urlParts.length >= 2) {
        // Remove 'api' prefix and join remaining parts
        urlParts.shift();
        chatbot_url = `https://${agencySlug}.${urlParts.join('.')}/snippet.js`;
      } else {
        // Fallback to default domain if URL parsing fails
        chatbot_url = `https://${agencySlug}.sensehqchat.com/snippet.js`;
      }
      break;
    case 'staging':
    default:
      chatbot_url = `https://${agencySlug}${getChatbotStagingDomain(
        hostname,
      )}.sensehqchat.com/snippet.js`;
      break;
  }

  return chatbot_url;
}

export const useFAQServicePath = (env: {
  agencySlug: string;
  serverRuntimeEnvironment: string;
  apiUrl: string;
  hostname: string;
  chatbotApiUrl: string;
}): string => {
  const {serverRuntimeEnvironment, hostname, chatbotApiUrl} = env;
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];

  let faq_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
      faq_url = `https://chatbot-faqs.sandbox-preprod.sensehq.com/api/faqs`;
      break;
    case 'staging':
      faq_url = `https://chatbot-faqs.${senseStagingDomain}.sensehq.co/api/faqs`;
      break;
    case 'production':
    case 'development':
    default:
      const urlParts = chatbotApiUrl.split('.');
      const extension = urlParts[urlParts.length - 1] === 'eu' ? 'eu' : 'co';
      faq_url = `https://chatbot-faqs.sensehq.${extension}/api/faqs`;
      break;
  }

  return faq_url;
};

export const useFAQV2ServicePath = (env: {
  agencySlug: string;
  serverRuntimeEnvironment: string;
  apiUrl: string;
  hostname: string;
  chatbotApiUrl: string;
}): string => {
  const {serverRuntimeEnvironment, hostname, chatbotApiUrl} = env;
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];

  let faq_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
      faq_url = `https://chatbot-faqs.sandbox-preprod.sensehq.com/api/v2`;
      break;
    case 'staging':
      faq_url = `https://chatbot-faqs.${senseStagingDomain}.sensehq.co/api/v2`;
      break;
    case 'production':
    case 'development':
    default:
      const urlParts = chatbotApiUrl.split('.');
      const extension = urlParts[urlParts.length - 1] === 'eu' ? 'eu' : 'co';
      faq_url = `https://chatbot-faqs.sensehq.${extension}/api/v2`;
      break;
  }

  return faq_url;
};

export const useChatbotLLMToolsServicePath = (): string => {
  const reduxStore = useStore();
  const store = reduxStore.getState();
  const env = store.env ?? {};
  const {serverRuntimeEnvironment, hostname, chatbotApiUrl} = env;

  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];

  const pathname = 'api/v1/llm-tools/';

  let llmToolsPath;
  switch (serverRuntimeEnvironment) {
    case 'preprod':
      llmToolsPath = `https://chatbot-llm-tools.sandbox-preprod.sensehq.com/${pathname}`;
      break;
    case 'staging':
      llmToolsPath = `https://chatbot-llm-tools.${senseStagingDomain}.sensehq.co:9041/${pathname}`;
      break;
    case 'production':
    case 'development':
    default:
      const urlParts = chatbotApiUrl.split('.');
      const extension = urlParts[urlParts.length - 1] === 'eu' ? 'eu' : 'com';
      llmToolsPath = `https://chatbot-llm-tools.sensehq.${extension}/${pathname}`;
      break;
  }

  return llmToolsPath;
};

type WritebackOption = {
  key: string;
  name?: string;
  label?: string;
  entity_type?: string;
  rteValue?: string;
  entityRelationshipPath?: Array<string>;
  [key: string]: unknown;
};

type WritebackAttribute = {
  attributeName?: string;
  name?: string;
  mapping: {
    display_name: string;
  };
  rteValue?: string;
  entityRelationshipPath?: Array<string>;
  [key: string]: unknown;
};

export const updateEditorContentWithPlaceholders = (
  template: string,
  placeholders: {
    [key: string]: string;
  },
  writebackOptions: Array<WritebackOption>,
  entityType: string,
): EditorState => {
  let contentState = ContentState.createFromText(template);

  const isVariable = (
    value: string,
    options: Array<WritebackOption>,
  ): boolean => {
    return options.some((option) => option.key === value) || false;
  };

  Object.keys(placeholders).forEach((key) => {
    const value = placeholders[key];
    if (value) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      const plainText = contentState.getPlainText();

      let match;

      while ((match = regex.exec(plainText)) !== null) {
        const start = match?.index ?? 0;
        const end = start + (match ? match[0].length : 0);

        // Create a selection range for the match
        const selection = contentState.getSelectionAfter().merge({
          anchorOffset: start,
          focusOffset: end,
        });

        if (isVariable(value, writebackOptions)) {
          // Create an entity for variable values
          const formattedValue = `{{${value}}}`;
          const contentStateWithEntity = contentState.createEntity(
            'DYNAMIC_LABEL',
            'IMMUTABLE',
            {entity_type: entityType, value},
          );
          const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

          // Replace the text with the variable value and add the entity
          contentState = Modifier.replaceText(
            contentState,
            selection,
            formattedValue,
            // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'DraftInlineStyle | undefined'.
            null,
            entityKey,
          );
        } else {
          // Replace with plain text if not a variable value
          contentState = Modifier.replaceText(contentState, selection, value);
        }
      }
    }
  });

  return EditorState.createWithContent(contentState);
};

export const createWhatsappTextDraftJson = (
  template: {
    body: string;
    id: string;
    [key: string]: unknown;
  },
  placeholderValues: {
    [key: string]: string;
  },
  hasPlaceholders: boolean,
): {
  whatsapp_template_id: string;
  whatsapp_template_message: string;
  whatsapp_fields: {
    [key: string]: string;
  };
} => {
  return {
    whatsapp_template_id: template.id,
    whatsapp_template_message: template.body,
    whatsapp_fields: hasPlaceholders ? placeholderValues : {},
  };
};

export const handleChatbotTemplateInsert = (
  template: {
    body: string;
    id: string;
    [key: string]: unknown;
  },
  placeholderValues: {
    [key: string]: string;
  },
  hasPlaceholders: boolean,
  chatbotOverWhatsappDetails: {
    additional_config_json?: {
      [key: string]: unknown;
    };
    whatsapp_text_draft_json?: {
      [key: string]: unknown;
    };
    [key: string]: unknown;
  },
  getDraftContentJson: () => unknown,
  dispatch: (action: unknown) => unknown,
  onClose: () => void,
): void => {
  const whatsappTextDraftJson = getDraftContentJson();
  const whatsappConfig = createWhatsappTextDraftJson(
    template,
    placeholderValues,
    hasPlaceholders,
  );
  const existingAdditionalConfig =
    chatbotOverWhatsappDetails.additional_config_json || {};
  const existingTextDraftJson =
    chatbotOverWhatsappDetails.whatsapp_text_draft_json || {};

  const updatedDetails = {
    ...chatbotOverWhatsappDetails,
    additional_config_json: {
      ...existingAdditionalConfig,
      ...whatsappConfig,
    },
    whatsapp_text_draft_json: {
      ...existingTextDraftJson,
      // @ts-expect-error - TS2698 - Spread types may only be created from object types.
      ...whatsappTextDraftJson,
    },
  } as const;
  dispatch(setWhatsappDetails(updatedDetails));
  onClose();
};

export function getFilteredOptions(
  writebackOptions: Array<WritebackOption>,
  entityType: string,
): Array<WritebackOption> {
  return writebackOptions
    .filter((option) =>
      entityType === 'Global Variable'
        ? option.entity_type === 'Global Variable'
        : option.entity_type !== 'Global Variable',
    )
    .map((option) => ({
      label: option.label || '',
      key: option.key,
      rteValue: option.rteValue || '',
      name: option.name || '',
      secondaryLabel:
        option.entityRelationshipPath?.slice(1).reverse().join('/') || '',
    }));
}

export function getLabelFromKey(
  writebackOptions: Array<WritebackOption>,
  key: string,
): string {
  return (
    writebackOptions.find((option) => option.key === key)?.label || key || ''
  );
}

export function processWritebackAttributes(
  writebackAttributes: Array<WritebackAttribute>,
): {
  writebackAttributes: Array<WritebackAttribute>;
  writebackOptions: Array<WritebackOption>;
} {
  const writebackOptions =
    writebackAttributes.map((data) => ({
      key: `${(data.attributeName ?? data.name) || ''}`,
      name: (data.attributeName ?? data.name) || '',
      label: `<${(data.attributeName ?? data.name) || ''}>`,
      entity_type: data.mapping.display_name || '',
      rteValue: data.rteValue || '',
      entityRelationshipPath: data.entityRelationshipPath || [],
    })) || [];

  return {
    writebackAttributes,
    writebackOptions,
  };
}
